import HttpClient from '@/utils/HttpClient'
import {Device, DeviceDraft} from '@/models/Device'
import {DeviceGroup, DeviceGroupDraft} from '@/models/DeviceGroup'
import {Scene} from '@/types/Scene'
import {PowerPacket} from '@/models/PowerPacket'
import {User, UserDraft} from '@/models/User'
import {Event} from '@/models/Event'
import {DetectorPacket} from '@/models/DetectorPacket'

export default class Api
{
    constructor(httpClient: HttpClient) {
        this.client = httpClient
    }

    client: HttpClient

    // COMMON: AUTH

    setUserToken(token: string | null) {
        this.client.setAuthToken(token)
    }

    setUnauthorizedHandler(handler: () => void) {
        this.client.onUnauthorized = handler
    }

    logIn(model: { username: string, password: string }): Promise<{ user: User }> {
        return this.client.post('user/log-in', model) as Promise<{ user: User }>
    }


    // EVENTS

    listEvents(filters: object, signal: AbortSignal|undefined = undefined): Promise<Event[]> {
        return this.client.post<Event[]>('event/list', filters, signal)
    }

    unactualizeEvent(id: number): Promise<Event> {
        return this.client.post<Event>('event/unactualize', {id})
    }

    // DEVICE GROUPS

    listDeviceGroups(filters: object): Promise<DeviceGroup[]> {
        return this.client.post<DeviceGroup[]>('device-group/list', filters)
    }

    getDeviceGroup(id: number): Promise<DeviceGroup> {
        return this.client.get<DeviceGroup>('device-group/get-group', {id})
    }

    setDeviceGroup(model: DeviceGroupDraft): Promise<DeviceGroup> {
        return this.client.post<DeviceGroup>('device-group/set-group', model)
    }

    deleteDeviceGroup(id: number): Promise<boolean> {
        return this.client.post<boolean>('device-group/delete-group', {id})
    }

    // DEVICES

    listDevices(filters: object, signal: AbortSignal|undefined = undefined): Promise<Device[]> {
        return this.client.post<Device[]>('device/list', filters, signal)
    }

    getDevice(id: number, expand: string[] = []): Promise<Device> {
        return this.client.get<Device>('device/get-device', {id, expand})
    }

    setDevice(model: DeviceDraft): Promise<Device> {
        return this.client.post<Device>('device/set-device', model)
    }

    resetDeviceState(id: number): Promise<Device> {
        return this.client.post<Device>('device/reset-state', {id})
    }

    deleteDevice(id: number): Promise<boolean> {
        return this.client.post<boolean>('device/delete-device', {id})
    }

    listDeviceTypes(): Promise<object[]> {
        return this.client.get<object[]>('device/list-device-types')
    }

    listDeviceLinkTypes(): Promise<object[]> {
        return this.client.get<object[]>('device/list-link-types')
    }

    // POWER PACKETS

    listPowerPackets(filters: object): Promise<PowerPacket[]> {
        return this.client.post<PowerPacket[]>('power-packet/list', filters)
    }

    getPowerPacket(id: number, expand: string[] = []): Promise<PowerPacket> {
        return this.client.get<PowerPacket>('power-packet/get-packet', {id, expand})
    }

    // DETECTOR PACKETS

    listDetectorPackets(filters: object): Promise<DetectorPacket[]> {
        return this.client.post<DetectorPacket[]>('detector-packet/list', filters)
    }

    getDetectorPacket(id: number, expand: string[] = []): Promise<DetectorPacket> {
        return this.client.get<DetectorPacket>('detector-packet/get-packet', {id, expand})
    }

    // SCENE

    getScene(): Promise<Scene> {
        return this.client.get<Scene>('scene/get-scene')
    }

    // USERS

    listUsers(filters: object): Promise<User[]> {
        return this.client.post<User[]>('user/list', filters)
    }

    getUser(id: number): Promise<User> {
        return this.client.get<User>('user/get-user', {id})
    }

    setUser(model: UserDraft): Promise<User> {
        return this.client.post<User>('user/set-user', model)
    }

    deleteUser(id: number): Promise<boolean> {
        return this.client.post<boolean>('user/delete-user', {id})
    }
}
